import { useQuery } from "@apollo/client";
import { Currency } from "dinero.js";
import moment from "moment";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
} from "recharts";
import { api } from "src/api";
import { BaseClientFields } from "src/api/fragments";
import { TaxYearBracket } from "src/api/generated/types";
import { useTheme } from "src/hooks/useTheme";
import { getGainsLossesLink } from "src/modules/ledger/transactions";
import { colors } from "src/theme";
import { D } from "src/utils/helpers";
import { CustomTooltip, RoundedBar } from "./components";

export const CapitalGainsChart = ({
  year,
  clientId,
  showXAxis = true,
  height = 150,
}: {
  year: TaxYearBracket;
  clientId: string;
  showXAxis?: boolean;
  height?: number;
}) => {
  const navigate = useNavigate();

  const { data: clientData } = useQuery<{
    getClientById?: BaseClientFields;
  }>(api.clients.retrieve, {
    fetchPolicy: "cache-only",
    variables: { clientId },
  });

  const currency = clientData?.getClientById?.currency as Currency;

  const variables = useMemo(
    () => ({
      year: year.taxYear,
      clientId,
    }),
    [year, clientId]
  );

  const { data } = useQuery(api.reports.monthlyPoints, {
    variables,
    fetchPolicy: "cache-and-network",
  });

  const points = data?.getMonthlyPoints ?? [];
  const theme = useTheme();

  const chartData = points.map((point: any) => ({
    timestamp: moment(point.timestamp).utc().format("MMM"),
    Capital_Gains: D(point.totalCapGainsCents).toUnit(),
    _data: point,
    // totalCapGainsCents: point.totalCapGainsCents,
  }));

  const _openCapGains = (entry: any) => {
    const start = moment(entry._data.timestamp).utc();
    const end = start.clone().add(1, "month");

    const link = getGainsLossesLink({
      clientId: clientId || "",
      startDate: start.toDate(),
      endDate: end.toDate(),
    });

    navigate(link);
  };

  if (!chartData.length) {
    return null;
  }

  return (
    <ResponsiveContainer
      className={"rechart-" + theme.theme}
      style={{
        // background: theme.background,
        borderRadius: 15,
        marginBottom: 25,
      }}
      width="100%"
      height={height}
    >
      <BarChart data={chartData}>
        {showXAxis && (
          <XAxis tickLine={false} axisLine={false} dataKey="timestamp" />
        )}
        <Tooltip
          content={
            <CustomTooltip currency={currency} field="totalCapGainsFormatted" />
          }
        />

        <Bar
          dataKey="Capital_Gains"
          // make the bar at least 5 high
          minPointSize={3}
          shape={(props: any) => (
            <RoundedBar
              {...props}
              radius={10}
              onClick={() => _openCapGains(props.payload)}
            />
          )}
        >
          {chartData.map((entry: any, index: number) => (
            <Cell
              key={`cell-${index}`}
              fill={
                entry["Capital_Gains"] === 0
                  ? theme.ternaryBackground
                  : entry["Capital_Gains"] > 0
                  ? colors.positive
                  : colors.negative
              }
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
};
